import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import React from 'react';
import { useEffect, useRef, useState } from 'react';

import { MediaPlayer, MediaProvider } from '@vidstack/react';
import { DefaultAudioLayout, defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import type { MediaPlayerInstance } from '@vidstack/react';

interface VdoPlayerProps {
  videoUrl: string;
}

function VdoPlayer({ videoUrl }: VdoPlayerProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;

    const initializePlayer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!videoUrl) {
          throw new Error('No video URL provided');
        }

        const fullVideoUrl = `${process.env.REACT_APP_API_URL}/uploads/${videoUrl}`;
        const response = await fetch(fullVideoUrl, { method: 'HEAD' });
        
        if (!response.ok) {
          throw new Error('Video not found');
        }

        if (mounted) {
          setIsPlayerReady(true);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Video initialization error:', err);
        if (mounted) {
          setError(err.message || 'Failed to load video');
          setIsLoading(false);
        }
      }
    };

    initializePlayer();

    return () => {
      mounted = false;
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error('Error destroying player:', e);
        }
      }
    };
  }, [videoUrl]);

  if (error) {
    return (
      <div style={{ 
        width: '100%', 
        aspectRatio: '16/9', 
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        {error}
      </div>
    );
  }

  if (isLoading || !isPlayerReady) {
    return (
      <div style={{ 
        width: '100%', 
        aspectRatio: '16/9', 
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        Loading...
      </div>
    );
  }
  
  /*return (
    <MediaPlayer 
      title="Sprite Fight" 
      src={fullVideoUrl}
      style={{ width: '100%', aspectRatio: '16/9' }}
    >
      <MediaProvider />
      <DefaultAudioLayout icons={defaultLayoutIcons} />
      <DefaultVideoLayout 
        //thumbnails="https://files.vidstack.io/sprite-fight/thumbnails.vtt" 
        icons={defaultLayoutIcons} />
    </MediaPlayer>
  );*/
  
  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {!error && (
        <MediaPlayer
          ref={playerRef}
          title="Lesson Video"
          src={`${process.env.REACT_APP_API_URL}/uploads/${videoUrl}`}
          style={{ width: '100%', aspectRatio: '16/9' }}
          onError={(e) => {
            console.error('Media player error:', e);
            setError('Error playing video');
          }}
          crossOrigin=""
          autoPlay={false}
          load="eager"
        >
          <MediaProvider />
          <DefaultVideoLayout icons={defaultLayoutIcons} />
        </MediaPlayer>
      )}
    </div>
  );
}

export default VdoPlayer;
